import React from 'react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import RemoveButton from '../components/RemoveButton';
import type { CompanionRow } from '../form/types';

interface CompanionRowItemProps {
  index: number;
  row: CompanionRow;
  canEdit: boolean;
  isCreating: boolean;
  isSyncing: boolean;
  onFieldChange: (
    index: number,
    field: keyof CompanionRow,
    value: string | boolean,
  ) => void;
  onCreateIntake: (index: number) => void;
  onRemoveRow: (index: number) => void;
  onSync: (targetIntakeId: string, clientName: string) => void;
}

export const CompanionRowItem: React.FC<CompanionRowItemProps> = ({
  index,
  row,
  canEdit,
  isCreating,
  isSyncing,
  onFieldChange,
  onCreateIntake,
  onRemoveRow,
  onSync,
}) => {
  const isCreateDisabled =
    !canEdit || !!row.createdIntakeId || !row.clientName.trim();
  return (
    <div className="relative border rounded-lg p-4">
      {/* Top-right remove action for consistency with Treatment section */}
      {!row.createdIntakeId && canEdit && (
        <RemoveButton onClick={() => onRemoveRow(index)} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Client Name */}
        <div className="md:col-span-4 space-y-2">
          <Label htmlFor={`companion-name-${index}`}>Client Name *</Label>
          <Input
            id={`companion-name-${index}`}
            value={row.clientName}
            onChange={(e) => onFieldChange(index, 'clientName', e.target.value)}
            disabled={!canEdit}
            placeholder="Full name"
            className="h-10"
          />
        </div>

        {/* Details */}
        <div className="md:col-span-6">
          <Label htmlFor={`companion-details-${index}`}>Details/Notes</Label>
          <Textarea
            id={`companion-details-${index}`}
            value={row.details || ''}
            onChange={(e) => onFieldChange(index, 'details', e.target.value)}
            disabled={!canEdit}
            placeholder="Relationship, injuries, notes from call"
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className="md:col-span-2 flex flex-col items-end gap-2 pt-6">
          {row.createdIntakeId ? (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        onSync(row.createdIntakeId as string, row.clientName)
                      }
                      disabled={isSyncing || !canEdit}
                      className="w-[90px]"
                    >
                      Sync
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Sync client name, Accident, Insurance, and Impact sections
                      from primary
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <a
                href={`/intakes/${row.createdIntakeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline px-2 py-2 rounded-md hover:bg-blue-50 transition-colors w-[110px] text-right"
              >
                View Intake →
              </a>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => onCreateIntake(index)}
              disabled={isCreating || isCreateDisabled}
              className="w-[120px]"
            >
              Create Intake
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanionRowItem;
